import { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { RouteFallback } from "@/app/RouteFallback";

import { keyedRouteOutletKey } from "./keyedRouteOutletKey";

/**
 * RR v7 navigations run in `startTransition`, so a reused Suspense boundary
 * keeps the previous route visible until the next chunk resolves — and the URL
 * can already show the new path. Keying Suspense (not just Outlet) resets the
 * boundary on each navigation so the matched page swaps reliably.
 *
 * The router itself now runs with `useTransitions={false}` (see `main.tsx`), so
 * location commits track the URL synchronously. Keying both the Suspense
 * boundary and the `Outlet` is defense in depth: it guarantees a full remount
 * of the matched route tree on every navigation (including list → detail and
 * prefetched chunks), so no previous page can linger.
 *
 * @see https://github.com/remix-run/react-router/discussions/12773
 */
export function ShellRouteOutlet() {
  const location = useLocation();
  const routeKey = keyedRouteOutletKey(location);

  return (
    <Suspense key={routeKey} fallback={<RouteFallback />}>
      <div
        data-route-key={routeKey}
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <Outlet key={routeKey} />
      </div>
    </Suspense>
  );
}
